import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { getTrainingPersona, updateTrainingPersona } from '../../services/personaLibraryApi';

const splitList = (text) => text.split(',').map(s => s.trim()).filter(Boolean);

/**
 * Edit the patient persona behind a training scenario (6 Oct 2026): who the patient is and
 * how they talk. Lecturers can edit personas that belong only to their group; a persona
 * used elsewhere (another group's scenario, advanced simulation) must be customised first. Advanced simulation layers
 * stay in the admin persona editor.
 */
const PersonaEditDialog = ({ open, personaId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [usedBy, setUsedBy] = useState(0);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open || !personaId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTrainingPersona(personaId)
      .then(data => {
        if (cancelled) return;
        const p = data.persona || {};
        const demo = p.metadata?.demographics || {};
        setCanEdit(!!data.canEdit);
        setUsedBy(data.usedByScenarios || 0);
        setForm({
          name: p.name || '',
          description: p.description || '',
          opening_line: p.opening_line || '',
          system_prompt: p.system_prompt || '',
          conditions: (p.metadata?.conditions || []).join(', '),
          severity: p.metadata?.severity || '',
          ageRange: demo.ageRange || '',
          gender: demo.gender || '',
          occupation: demo.occupation || '',
          culturalContextSummary: demo.culturalContextSummary || '',
          languages: (demo.languages || []).join(', ')
        });
      })
      .catch(err => { if (!cancelled) setError(err?.msg || err?.message || 'Could not load the persona'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, personaId]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await updateTrainingPersona(personaId, {
        name: form.name,
        description: form.description,
        opening_line: form.opening_line,
        system_prompt: form.system_prompt,
        metadata: {
          conditions: splitList(form.conditions),
          severity: form.severity || null,
          demographics: {
            ageRange: form.ageRange,
            gender: form.gender,
            occupation: form.occupation,
            culturalContextSummary: form.culturalContextSummary || null,
            languages: splitList(form.languages)
          }
        }
      });
      onSaved(data.persona);
    } catch (err) {
      setError(err?.msg || err?.message || 'Could not save the persona');
    } finally {
      setSaving(false);
    }
  };

  const readOnly = !canEdit;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit patient persona</DialogTitle>
      <DialogContent dividers>
        {loading || !form ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {readOnly ? (
              <Alert severity="info">
                This persona is also used outside your group (another group&apos;s scenario or advanced simulation),
                so changing it would change those too. Close this and choose &quot;Customise for this scenario&quot; to
                get your own copy, then edit that.
              </Alert>
            ) : usedBy > 1 && (
              <Alert severity="warning">
                {usedBy} scenarios in your group use this persona. Changes apply to all of them.
              </Alert>
            )}
            <TextField id="persona-name" label="Name" value={form.name} onChange={set('name')} disabled={readOnly} fullWidth />
            <TextField id="persona-description" label="Summary" value={form.description} onChange={set('description')} disabled={readOnly} fullWidth multiline minRows={2} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField id="persona-age" label="Age range" value={form.ageRange} onChange={set('ageRange')} disabled={readOnly} sx={{ flex: '1 1 140px' }} />
              <TextField id="persona-gender" label="Gender" value={form.gender} onChange={set('gender')} disabled={readOnly} sx={{ flex: '1 1 140px' }} />
              <TextField id="persona-occupation" label="Occupation" value={form.occupation} onChange={set('occupation')} disabled={readOnly} sx={{ flex: '1 1 180px' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField id="persona-background" label="Cultural background (short)" value={form.culturalContextSummary} onChange={set('culturalContextSummary')} disabled={readOnly} sx={{ flex: '1 1 220px' }} />
              <TextField id="persona-languages" label="Languages (comma separated)" value={form.languages} onChange={set('languages')} disabled={readOnly} sx={{ flex: '1 1 220px' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField id="persona-conditions" label="Conditions (comma separated)" value={form.conditions} onChange={set('conditions')} disabled={readOnly} sx={{ flex: '2 1 260px' }} />
              <TextField id="persona-severity" label="Severity" value={form.severity} onChange={set('severity')} disabled={readOnly} placeholder="mild, moderate or severe" sx={{ flex: '1 1 160px' }} />
            </Box>
            <TextField id="persona-opening" label="Opening line" helperText="What the patient says first" value={form.opening_line} onChange={set('opening_line')} disabled={readOnly} fullWidth multiline minRows={2} />
            <TextField id="persona-prompt" label="How to play the patient" helperText="The instructions the AI follows to play this patient" value={form.system_prompt} onChange={set('system_prompt')} disabled={readOnly} fullWidth multiline minRows={10} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button variant="contained" onClick={handleSave} disabled={saving || !form || !form.name.trim() || !form.system_prompt.trim()}>
            {saving ? 'Saving...' : 'Save persona'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PersonaEditDialog;
